import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// Admin Pages
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import ExamListPage from './pages/admin/ExamListPage';
import ExamCreatePage from './pages/admin/ExamCreatePage';
import ExamDetailPage from './pages/admin/ExamDetailPage';
import QuestionManagerPage from './pages/admin/QuestionManagerPage';
import CandidatesPage from './pages/admin/CandidatesPage';
import ResultsPage from './pages/admin/ResultsPage';
import LiveMonitorPage from './pages/admin/LiveMonitorPage';
import ShortlistPage from './pages/admin/ShortlistPage';

// Super Admin Pages
import SuperAdminLayout from './pages/admin/SuperAdminLayout';
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import AdminsManagePage from './pages/admin/AdminsManagePage';

// Candidate Pages
import CandidateLayout from './pages/candidate/CandidateLayout';
import CandidateDashboard from './pages/candidate/CandidateDashboard';
import ExamListCandidatePage from './pages/candidate/ExamListCandidatePage';
import ExamInterface from './pages/candidate/ExamInterface';
import CandidateResultsPage from './pages/candidate/CandidateResultsPage';
import ResultDetailPage from './pages/candidate/ResultDetailPage';
import ProfilePage from './pages/candidate/ProfilePage';

// Route Guards
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to={user?.role === 'candidate' ? '/candidate' : '/admin'} replace />;
  }
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (isAuthenticated) {
    const dest = user?.role === 'candidate' ? '/candidate' : user?.role === 'super_admin' ? '/super-admin' : '/admin';
    return <Navigate to={dest} replace />;
  }
  return children;
};

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-navy/20 border-t-navy rounded-full animate-spin mx-auto mb-3" />
      <p className="text-gray-500 font-medium">Loading...</p>
    </div>
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public auth routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

      {/* Admin routes */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin','super_admin']}><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="exams" element={<ExamListPage />} />
        <Route path="exams/create" element={<ExamCreatePage />} />
        <Route path="exams/:id" element={<ExamDetailPage />} />
        <Route path="exams/:id/questions" element={<QuestionManagerPage />} />
        <Route path="exams/:id/monitor" element={<LiveMonitorPage />} />
        <Route path="candidates" element={<CandidatesPage />} />
        <Route path="results" element={<ResultsPage />} />
        <Route path="shortlist" element={<ShortlistPage />} />
      </Route>

      {/* Super Admin routes */}
      <Route path="/super-admin" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminLayout /></ProtectedRoute>}>
        <Route index element={<SuperAdminDashboard />} />
        <Route path="admins" element={<AdminsManagePage />} />
      </Route>

      {/* Candidate routes */}
      <Route path="/candidate" element={<ProtectedRoute allowedRoles={['candidate']}><CandidateLayout /></ProtectedRoute>}>
        <Route index element={<CandidateDashboard />} />
        <Route path="exams" element={<ExamListCandidatePage />} />
        <Route path="results" element={<CandidateResultsPage />} />
        <Route path="results/:attemptId" element={<ResultDetailPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* Exam interface - full screen, no layout */}
      <Route path="/exam/:examId" element={<ProtectedRoute allowedRoles={['candidate']}><ExamInterface /></ProtectedRoute>} />

      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const RootRedirect = () => {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const dest = user?.role === 'candidate' ? '/candidate' : user?.role === 'super_admin' ? '/super-admin' : '/admin';
  return <Navigate to={dest} replace />;
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '14px', fontWeight: '500' },
            success: { iconTheme: { primary: '#059669', secondary: '#fff' } },
            error: { iconTheme: { primary: '#dc2626', secondary: '#fff' } },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}
